import React, { useState, useEffect, useRef } from "react";
import { PrimaryButtonSmall, SecondaryButtonSmall } from "../../components/Button";
import Loader from "../../components/Loader";
import GeneratingPhotoSkeleton from "./GeneratingPhotoSkeleton";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

const PhotoModal = ({ photo, onClose, onRemove, onDeleteFromStorage }) => {
  const [isLoading, setIsLoading] = useState(true);

  const url = typeof photo === "string" ? photo : URL.createObjectURL(photo);

  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.onload = () => setIsLoading(false);
  }, [url]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl w-full max-h-full">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 sm:h-6 sm:w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="relative bg-white/20 backdrop-blur-md border border-white/30 rounded-lg overflow-hidden shadow-2xl">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <Loader />
            </div>
          ) : (
            <img
              src={url}
              alt="Enlarged preview"
              className="w-full h-auto max-h-[80vh] object-cover"
            />
          )}
          <div className=" p-4  ">
            <div className="flex justify-center gap-4">
              <button
                onClick={onRemove}
                className="text-xs sm:text-base px-2 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 font-medium flex items-center"
              >
                Remove from Event
              </button>
              {typeof photo === "string" &&
                photo.startsWith(
                  "https://firebasestorage.googleapis.com/"
                ) && (
                  <button
                    onClick={onDeleteFromStorage}
                    className="text-xs sm:text-base px-4 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 font-medium flex items-center"
                  >
                    Delete from Storage
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PhotosInput = ({
  photos,
  onPhotoChange,
  removePhoto,
  onDeleteFromStorage,
  onSelectFromLibrary,
  onGenerateImage,
  generatingImage,
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageKeywords, setImageKeywords] = useState("");
  const [showKeywords, setShowKeywords] = useState(false);
  const keywordsRef = useRef(null);

  // Close the keyword dropdown when clicking outside of it.
  useEffect(() => {
    if (!showKeywords) return;
    const handlePointerDown = (e) => {
      if (keywordsRef.current && !keywordsRef.current.contains(e.target)) {
        setShowKeywords(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showKeywords]);

  const photoPreviews = photos?.map((photo, index) => {
    const url =
      typeof photo === "string" ? photo : URL.createObjectURL(photo);
    return (
      <div
        className="relative w-24 h-24 rounded-md shadow-md overflow-hidden cursor-pointer"
        key={index}
        onClick={() => {
          setSelectedPhotoIndex(index);
          setIsModalOpen(true);
        }}
      >
        <img
          src={variantURL(url, 400)}
          onError={fallbackToOriginal(url)}
          alt={`preview-${index}`}
          className="w-full h-full object-cover"
        />
      </div>
    );
  });

  return (
    <div>
      <label
        htmlFor="photos"
        className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2"
      >
        Photos
      </label>
      <div className="flex gap-2 sm:gap-4 mb-4">
        <div className="relative sm:max-w-40 w-1/3" ref={keywordsRef}>
          <div className="flex">
            <SecondaryButtonSmall
              type="button"
              onClick={() => onGenerateImage(imageKeywords.trim())}
              loading={generatingImage}
              className="flex items-center justify-center !p-2 flex-1 min-w-0 !rounded-r-none text-sm sm:text-base"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 sm:mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              <span className="hidden sm:inline-block truncate min-w-0">
                {generatingImage ? "Generating..." : "Generate"}
              </span>
            </SecondaryButtonSmall>
            <button
              type="button"
              onClick={() => setShowKeywords((v) => !v)}
              aria-label="Customize generated image with keywords"
              aria-expanded={showKeywords}
              className="relative flex flex-shrink-0 items-center justify-center px-2 bg-white dark:bg-gray-800 text-primary border border-l-0 border-primary rounded-r hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform ${showKeywords ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              {imageKeywords.trim() && !showKeywords && (
                <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-primary" />
              )}
            </button>
          </div>
          {showKeywords && (
            <div className="absolute left-0 z-20 mt-2 w-64 max-w-[80vw] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-3">
              <label
                htmlFor="image-keywords"
                className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
              >
                Customize image
              </label>
              <input
                id="image-keywords"
                type="text"
                autoFocus
                value={imageKeywords}
                onChange={(e) => setImageKeywords(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onGenerateImage(imageKeywords.trim());
                  }
                }}
                placeholder="e.g. rooftop sunset, string lights, cozy"
                className="px-3 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm"
              />
              <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                Keywords are added to the prompt when you tap Generate. Leave blank for an auto-generated image.
              </p>
            </div>
          )}
        </div>
        <PrimaryButtonSmall
          type="button"
          onClick={onSelectFromLibrary}
          className="!p-2 sm:max-w-40 w-1/3 text-sm sm:text-base"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Existing
        </PrimaryButtonSmall>

        <div className="relative flex items-center justify-center !p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer sm:max-w-40 sm:w-1/3 text-sm sm:text-base">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onPhotoChange}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mx-2 md:mr-2 "
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          <span className="hidden sm:block">Upload</span>
        </div>
      </div>
      <div className="flex mt-2 flex-wrap gap-4">
        {photoPreviews}
        {generatingImage && <GeneratingPhotoSkeleton />}
      </div>
      {generatingImage && (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Generating an image from this event's details. This usually takes 15-20 seconds.
        </p>
      )}
      {isModalOpen && selectedPhotoIndex !== null && (
        <PhotoModal
          photo={photos[selectedPhotoIndex]}
          onClose={() => setIsModalOpen(false)}
          onRemove={() => {
            removePhoto(selectedPhotoIndex);
            setIsModalOpen(false);
          }}
          onDeleteFromStorage={() => {
            onDeleteFromStorage(selectedPhotoIndex);
            setIsModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default PhotosInput;
